import React from 'react';
import { Spinner, Table } from 'react-bootstrap';
import { BulkImport, CreateUserModal, UserRow } from '~/components/Admin';
import { Pagination } from '~/components/Pagination';
import { ResultsHeader } from '~/components/SearchResults';
import { ServicesContext, useAdminService } from '~/services';
import { PaginationType, emptyPagination, useServiceSubscribe } from '~/util';
import { SearchUsers } from './SearchUsers';

const searchByDropdown = [
    { val: 'last_name', label: 'Last Name' },
    { val: 'email', label: 'Email' },
];

export function ListUsers() {
    const servicesContext = React.useContext(ServicesContext);
    const { getUsers, events } = useAdminService();
    const paginationRef = React.useRef(emptyPagination());
    const [loading, setLoading] = React.useState(false);
    const search = React.useRef(searchByDropdown[0]);
    const [sort, setSort] = React.useState({
        field: 'last_name',
        dir: 'ASC',
    });
    const [users, setUsers] = React.useState<UserType[]>([]);
    const [userSearch, setUserSearch] = React.useState('');

    const loadUsers = React.useCallback(async () => {
        const params = {
            q: '',
            page: paginationRef.current.current,
            items_per_page: paginationRef.current.items_per_page,
            [`order[${sort.field}]`]: sort.dir,
        };

        // Search
        if (userSearch) {
            params[search.current.val] = userSearch;
        }

        window.scrollTo(0, 0);
        setLoading(true);
        try {
            const { results, pagination } = await getUsers(params, {
                handledErrors: { 400: true },
            });
            setUsers(results);
            paginationRef.current = pagination;
        } catch (e) {
            servicesContext.alert.set(e.message, 'danger');
        } finally {
            setLoading(false);
        }
    }, [servicesContext.alert, getUsers, sort.dir, sort.field, userSearch]);

    async function handleSearchSubmit(query: string) {
        paginationRef.current.current = 1;
        // If they haven't actually changed the search val, force a search action
        // Changed query will fire the useEffect
        if (query === userSearch) {
            loadUsers();
        }
        setUserSearch(query);
    }

    function updateUser(user: UserType) {
        setUsers((prev) => prev.map((x) => (x.id === user.id ? user : x)));
    }

    function updatePagination(pagination: PaginationType) {
        paginationRef.current = { ...pagination };
        loadUsers();
    }

    function updateSort(field: string) {
        setSort((prev) => ({
            field,
            dir: field === prev.field && prev.dir === 'ASC' ? 'DESC' : 'ASC',
        }));
    }

    function setSearch(val: { val: string; label: string }) {
        search.current = val;
    }

    React.useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    useServiceSubscribe(events.user_created, loadUsers);
    useServiceSubscribe(events.user_updated, updateUser);

    return (
        <div>
            <div className="d-flex justify-content-between mb-2">
                <div>
                    <SearchUsers
                        dropdownItems={searchByDropdown}
                        onDropdownChange={setSearch}
                        onSubmit={handleSearchSubmit}
                    />
                </div>
                <div className="ml-2">
                    <BulkImport />
                    <CreateUserModal />
                </div>
            </div>
            {!loading && users.length === 0 ? (
                <div className="text-center my-5">
                    There are no results
                    <br />
                    for your current search
                </div>
            ) : (
                <>
                    <div className="table-scroll">
                        <Table striped bordered hover className="fixed-table">
                            <thead className="table-dark">
                                <tr>
                                    <ResultsHeader
                                        updateSort={updateSort}
                                        label="Name"
                                        field="last_name"
                                        sort={sort}
                                        className="th-11"
                                    />
                                    <ResultsHeader
                                        updateSort={updateSort}
                                        label="Email"
                                        field="email"
                                        sort={sort}
                                        className="th-16"
                                    />
                                    <ResultsHeader
                                        updateSort={updateSort}
                                        label="Title"
                                        field="title"
                                        sort={sort}
                                        className="th-11"
                                    />
                                    <ResultsHeader
                                        updateSort={updateSort}
                                        label="Date Created"
                                        field="created_at"
                                        sort={sort}
                                        className="th-11"
                                    />
                                    <ResultsHeader
                                        updateSort={updateSort}
                                        label="Last Login"
                                        field="last_login"
                                        sort={sort}
                                        className="th-11"
                                    />
                                    <th className="th-3">
                                        <span className="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loading &&
                                    users.map((user) => (
                                        <UserRow
                                            user={user}
                                            key={user.id}
                                            refreshUserList={loadUsers}
                                        />
                                    ))}
                                {loading && (
                                    <tr>
                                        <td colSpan={6} className="text-center bg-white">
                                            <Spinner animation="border">
                                                <span className="sr-only">Loading Users</span>
                                            </Spinner>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                    <Pagination
                        pagination={paginationRef.current}
                        updatePagination={updatePagination}
                    />
                </>
            )}
        </div>
    );
}
